import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Settings2 } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select } from '../ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Skeleton } from '../ui/skeleton'
import { toast } from '../../hooks/useToast'
import { getApiError } from '../../lib/api'
import { useUserPreferences, useUpdateUserPreferences } from '../../hooks/useSettings'
import { UserPreferencesUpdate } from '../../types/settings'

const CURRENCIES = [
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'RUB', label: 'RUB - Russian Ruble' },
]

const LANGUAGES = [
  { value: 'es', label: 'Espanol' },
  { value: 'en', label: 'English' },
  { value: 'ru', label: 'Russkiy' },
]

export function PreferencesForm() {
  const { t } = useTranslation()
  const { data: preferences, isLoading } = useUserPreferences()
  const updateMutation = useUpdateUserPreferences()

  const [formData, setFormData] = useState<UserPreferencesUpdate>({
    defaultCurrency: 'EUR',
    defaultLanguage: 'es',
    invoicePrefix: 'INV',
  })

  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    if (preferences) {
      setFormData({
        defaultCurrency: preferences.defaultCurrency,
        defaultLanguage: preferences.defaultLanguage,
        invoicePrefix: preferences.invoicePrefix,
      })
      setHasChanges(false)
    }
  }, [preferences])

  const handleChange = (field: keyof UserPreferencesUpdate, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync(formData)
      toast({
        title: t('settings.preferences.saved'),
        description: t('settings.preferences.savedDesc'),
      })
      setHasChanges(false)
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-60" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium">{t('settings.preferences.title')}</h3>
        <p className="text-sm text-muted-foreground">
          {t('settings.preferences.description')}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Settings2 className="h-4 w-4" />
            {t('settings.preferences.general')}
          </CardTitle>
          <CardDescription>
            {t('settings.preferences.generalDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="defaultCurrency">
              {t('settings.preferences.defaultCurrency')}
            </Label>
            <Select
              id="defaultCurrency"
              value={formData.defaultCurrency}
              onChange={(e) => handleChange('defaultCurrency', e.target.value)}
              disabled={updateMutation.isPending}
            >
              {CURRENCIES.map((currency) => (
                <option key={currency.value} value={currency.value}>
                  {currency.label}
                </option>
              ))}
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="defaultLanguage">
              {t('settings.preferences.defaultLanguage')}
            </Label>
            <Select
              id="defaultLanguage"
              value={formData.defaultLanguage}
              onChange={(e) => handleChange('defaultLanguage', e.target.value)}
              disabled={updateMutation.isPending}
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="invoicePrefix">
              {t('settings.preferences.invoicePrefix')}
            </Label>
            <Input
              id="invoicePrefix"
              value={formData.invoicePrefix}
              onChange={(e) => handleChange('invoicePrefix', e.target.value)}
              placeholder={t('settings.preferences.invoicePrefixPlaceholder')}
              maxLength={20}
              disabled={updateMutation.isPending}
            />
            <p className="text-xs text-muted-foreground">
              {t('settings.preferences.invoicePrefixHelp')}
            </p>
          </div>

          <div className="flex justify-end pt-4">
            <Button
              onClick={handleSave}
              disabled={!hasChanges || updateMutation.isPending}
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.saving')}
                </>
              ) : (
                t('common.save')
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
